import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Info } from "lucide-react";
import { 
  QualityScore, 
  getQualityLevelColor, 
  getQualityLevelBgColor,
  getQualityLevelLabel 
} from "@/lib/geo-quality-score";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface GeoQualityBadgeProps {
  qualityScore: QualityScore;
  compact?: boolean;
  showBreakdown?: boolean;
}

export function GeoQualityBadge({ 
  qualityScore, 
  compact = false,
  showBreakdown = true 
}: GeoQualityBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);

  const badge = (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border cursor-pointer transition-all",
        getQualityLevelBgColor(qualityScore.level),
        getQualityLevelColor(qualityScore.level)
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (showBreakdown) setIsOpen(true);
      }}
    >
      {compact ? (
        <span>{qualityScore.score}%</span>
      ) : (
        <>
          <span>{getQualityLevelLabel(qualityScore.level)}</span>
          <span className="opacity-70">({qualityScore.score}%)</span>
        </>
      )}
    </motion.div>
  );

  if (!showBreakdown) {
    return badge;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {badge}
      </PopoverTrigger>
      <PopoverContent 
        className="w-72 p-4 bg-card/95 backdrop-blur-md border-border/50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">GEO Quality Score</h4>
            <span className={cn(
              "text-lg font-bold",
              getQualityLevelColor(qualityScore.level)
            )}>
              {qualityScore.score}%
            </span>
          </div>

          {/* Progress Bar */}
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${qualityScore.score}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className={cn(
                "h-full rounded-full",
                qualityScore.level === 'verified' && "bg-brand-cyan",
                qualityScore.level === 'excellent' && "bg-purple-500",
                qualityScore.level === 'good' && "bg-blue-500",
                qualityScore.level === 'basic' && "bg-muted-foreground"
              )}
            />
          </div>

          {/* Breakdown */}
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {qualityScore.breakdown.map((item) => (
              <div 
                key={item.field}
                className="flex items-center justify-between text-xs"
              >
                <div className="flex items-center gap-2">
                  {item.completed ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <X className="h-3 w-3 text-muted-foreground" />
                  )}
                  <span className={cn(
                    item.completed ? "text-foreground" : "text-muted-foreground"
                  )}>
                    {item.field}
                  </span>
                </div>
                <span className={cn(
                  "font-mono",
                  item.completed ? "text-green-500" : "text-muted-foreground"
                )}>
                  +{item.points}
                </span>
              </div>
            ))}
          </div>

          {/* Suggestions */}
          {qualityScore.suggestions.length > 0 && (
            <div className="pt-2 border-t border-border/50">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                <Info className="h-3 w-3" />
                <span>Tips to improve</span>
              </div>
              <ul className="text-xs text-muted-foreground space-y-0.5">
                {qualityScore.suggestions.slice(0, 2).map((suggestion, i) => (
                  <li key={i} className="pl-3">• {suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
